import { useEffect, useState } from 'react';
import { CheckSquare } from 'lucide-react';
import {
  apiClient,
  type ApiError,
  type ActiveFilter,
  type TaskCategory,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Field, TextInput } from '@/components/clients/form-primitives';
import { ConfigModal } from './config-modal';
import { ListToolbar } from './list-toolbar';

interface FormState {
  name: string;
  color: string;
  icon: string;
  orderIndex: number;
  active: boolean;
}

const EMPTY: FormState = {
  name: '',
  color: '#3B82F6',
  icon: '',
  orderIndex: 0,
  active: true,
};

export function TaskCategoriesPage() {
  const [items, setItems] = useState<TaskCategory[]>([]);
  const [active, setActive] = useState<ActiveFilter>('true');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TaskCategory | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .taskCategoriesList({
        active: active === 'all' ? undefined : (active as 'true' | 'false'),
        search: search.trim() || undefined,
      })
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((e: ApiError) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, search]);

  function openNew() {
    setEditing(null);
    setForm({ ...EMPTY, orderIndex: items.length });
    setFormError(null);
    setModalOpen(true);
  }

  function openEdit(item: TaskCategory) {
    setEditing(item);
    setForm({
      name: item.name,
      color: item.color,
      icon: item.icon ?? '',
      orderIndex: item.orderIndex,
      active: item.active,
    });
    setFormError(null);
    setModalOpen(true);
  }

  async function submit() {
    if (!form.name.trim()) {
      setFormError('Nome obrigatório');
      return;
    }
    if (!/^#[0-9a-fA-F]{6}$/.test(form.color)) {
      setFormError('Cor deve ser hex (#RRGGBB)');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      if (editing) {
        await apiClient.taskCategoriesUpdate(editing.id, {
          name: form.name.trim(),
          color: form.color,
          icon: form.icon.trim() || null,
          orderIndex: form.orderIndex,
          active: form.active,
        });
      } else {
        await apiClient.taskCategoriesCreate({
          name: form.name.trim(),
          color: form.color,
          icon: form.icon.trim() || null,
          orderIndex: form.orderIndex,
          active: form.active,
        });
      }
      setModalOpen(false);
      const rows = await apiClient.taskCategoriesList({
        active: active === 'all' ? undefined : (active as 'true' | 'false'),
        search: search.trim() || undefined,
      });
      setItems(rows);
    } catch (e) {
      setFormError((e as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(item: TaskCategory) {
    if (!window.confirm(`Excluir a tarefa "${item.name}"?`)) return;
    try {
      await apiClient.taskCategoriesDelete(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      window.alert((e as ApiError).message);
    }
  }

  return (
    <div className="space-y-6">
      <ListToolbar
        title="Tarefas"
        description="Tipos de tarefa disponíveis para seleção (Reunião, Protocolo, Audiência, etc)."
        newLabel="Nova tarefa"
        onNew={openNew}
        active={active}
        onActiveChange={setActive}
        search={search}
        onSearchChange={setSearch}
      />

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Carregando…
          </p>
        ) : error ? (
          <p className="px-4 py-8 text-center text-sm text-red-500">{error}</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <CheckSquare className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">
              Nenhuma tarefa cadastrada. Clique em <strong>Nova tarefa</strong>.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium w-8" />
                <th className="px-4 py-2 font-medium">Nome</th>
                <th className="px-4 py-2 font-medium">Cor</th>
                <th className="px-4 py-2 font-medium">Ordem</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]"
                >
                  <td className="px-4 py-2">
                    <span
                      className="inline-block w-4 h-4 rounded-full"
                      style={{ background: item.color }}
                      aria-label="cor"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => openEdit(item)}
                      className="text-left font-medium text-[var(--fg)] hover:text-[var(--accent)]"
                    >
                      {item.name}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-[var(--fg-muted)]">
                    <code className="text-xs">{item.color}</code>
                  </td>
                  <td className="px-4 py-2 text-[var(--fg-muted)]">
                    {item.orderIndex}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        item.active
                          ? 'inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-500'
                          : 'inline-flex items-center rounded-full bg-zinc-500/15 px-2 py-0.5 text-xs font-medium text-zinc-400'
                      }
                    >
                      {item.active ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item)}
                    >
                      Excluir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar tarefa' : 'Nova tarefa'}
        description="Tipo de tarefa disponível para seleção no formulário."
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? 'Salvando…' : 'Salvar'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError && (
            <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
              {formError}
            </p>
          )}
          <Field label="Nome" required>
            <TextInput
              value={form.name}
              onChange={(e) =>
                setForm((s) => ({ ...s, name: e.target.value }))
              }
              placeholder="Ex: Reunião"
              maxLength={80}
            />
          </Field>
          <Field label="Cor (hex)" required>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.color}
                onChange={(e) =>
                  setForm((s) => ({ ...s, color: e.target.value.toUpperCase() }))
                }
                className="h-9 w-12 rounded border border-[var(--border)] cursor-pointer"
              />
              <TextInput
                value={form.color}
                onChange={(e) =>
                  setForm((s) => ({ ...s, color: e.target.value }))
                }
                placeholder="#3B82F6"
                maxLength={7}
              />
            </div>
          </Field>
          <Field label="Ordem">
            <TextInput
              type="number"
              value={String(form.orderIndex)}
              onChange={(e) =>
                setForm((s) => ({
                  ...s,
                  orderIndex: Number(e.target.value) || 0,
                }))
              }
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) =>
                setForm((s) => ({ ...s, active: e.target.checked }))
              }
              className="h-4 w-4 rounded border-[var(--border)]"
            />
            Ativa
          </label>
        </div>
      </ConfigModal>
    </div>
  );
}
